import AHDate from "_shared/dates/date";
import moment, { type Moment } from "moment";

/**
 * How a list reads a date or a moment in time, wherever it is drawn: a table cell, a phone card, or
 * one of the plain admin tables (#6713).
 *
 * Lists used at least seven formats for the same idea — `MM/DD/YY`, `MM/DD/YYYY`, `L`,
 * `toLocaleDateString()`, `MMM D, h:mm A`, UTC stamps and "Mar 31, 2027". These are the only ones a
 * list column draws now, and ESLint's `attorney-hub/list-cell-dates` refuses a format string written
 * in a list file instead.
 *
 * The month is a word, so "Mar 3, 2027" cannot be read day-first. Each function answers `undefined`
 * where there is no date, so a cell can draw `NoValueDash` rather than an empty string.
 *
 * This is the module #6935 extends to the reader's locale and time zone; add a format here, not beside it.
 */
export type DateValue = AHDate | Date | Moment | string | number | null | undefined;

const DATE = "MMM D, YYYY";
const DATE_IN_YEAR = "MMM D";
const TIME = "h:mm A";
const DATE_IN_YEAR_AND_TIME = "MMM D, h:mm A";
const FULL_TIMESTAMP = "MMM D, YYYY, h:mm A";

/** A calendar date: "Mar 3, 2027". */
export function formatDate(value: DateValue): string | undefined {
  return momentOf(value)?.format(DATE);
}

/**
 * A moment in time, as near as it needs to be: "2:15 PM" today, "Oct 9, 2:15 PM" this year, and
 * "Mar 3, 2025" before that. Show `formatFullTimestamp` beside it, in a tooltip.
 *
 * `now` is passed in rather than read, so every row of a list agrees about which day today is.
 */
export function formatTimestamp(value: DateValue, now: Date = new Date()): string | undefined {
  const date = momentOf(value);
  if (!date) return undefined;
  const today = moment(now);
  if (date.isSame(today, "day")) return date.format(TIME);
  if (date.isSame(today, "year")) return date.format(DATE_IN_YEAR_AND_TIME);
  return date.format(DATE);
}

/** The whole of a timestamp, for the tooltip a shortened one carries: "Mar 3, 2025, 2:15 PM". */
export function formatFullTimestamp(value: DateValue): string | undefined {
  return momentOf(value)?.format(FULL_TIMESTAMP);
}

/**
 * What a search over a timestamp column reads: every form `formatTimestamp` can draw for the value,
 * whatever day it is. "Mar 3, 2025, 2:15 PM · Mar 3, 2:15 PM" holds "2:15 PM", "Mar 3, 2:15 PM" and
 * "Mar 3, 2025", so a search for what the cell shows finds the row, and the text never changes at
 * midnight the way the drawn form does. A table caches an accessor's value per row, so a value that
 * read the clock would go stale while the cell moved on.
 */
export function timestampSearchText(value: DateValue): string | undefined {
  const date = momentOf(value);
  return date ? `${date.format(FULL_TIMESTAMP)} · ${date.format(DATE_IN_YEAR_AND_TIME)}` : undefined;
}

/**
 * A moment as the server records it, for an operator comparing it with logs and cutoffs that are kept
 * in UTC: "Mar 3, 2027, 2:15 PM UTC". The reader's own time would put it an offset away from them.
 */
export function formatUtcTimestamp(value: DateValue): string | undefined {
  const date = momentOf(value);
  return date ? `${date.clone().utc().format(FULL_TIMESTAMP)} UTC` : undefined;
}

/**
 * A span of dates, with the year said once when the span sits inside one: "Oct 3 – Dec 9, 2026", or
 * "Oct 3, 2026 – Jan 9, 2027". `undefined` unless both ends are dates.
 */
export function formatDateRange(start: DateValue, end: DateValue): string | undefined {
  const from = momentOf(start);
  const to = momentOf(end);
  if (!from || !to) return undefined;
  const startText = from.year() === to.year() ? from.format(DATE_IN_YEAR) : from.format(DATE);
  return `${startText} – ${to.format(DATE)}`;
}

/** The value as an ISO string, for a `<time dateTime>` attribute. */
export function isoDateTime(value: DateValue): string | undefined {
  return momentOf(value)?.toISOString();
}

function momentOf(value: DateValue): Moment | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const date = toMoment(value);
  return date.isValid() ? date : undefined;
}

function toMoment(value: AHDate | Date | Moment | string | number): Moment {
  if (value instanceof AHDate) return value.asMoment;
  if (moment.isMoment(value)) return value;
  // A string goes through AHDate, which parses ISO and the app's own display formats without moment's
  // `new Date()` fallback.
  if (typeof value === "string") return new AHDate(value).asMoment;
  return moment(value);
}
